/**
 * ElementRenderer - Central dispatcher for rendering canvas elements.
 *
 * Routes to the appropriate renderer based on element type.
 * Handles grid positioning for both root and nested elements.
 *
 * Ported from the original MyVault application.
 */

import React from "react";
import {
  Box,
  Typography,
  Input,
  Textarea,
  Select,
  Option,
  Checkbox,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  Button,
  Sheet,
  Link,
} from "@mui/joy";
import * as MuiIcons from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GridOnIcon from "@mui/icons-material/GridOn";
import type {
  CanvasElement,
  CanvasElementProps,
  ContainerElementConfig,
  FileReference,
} from "../../types";
import { formatFileSize } from "../../types";
import type { FieldState, RenderableElement } from "./CanvasRenderer";
import { SectionRenderer } from "./containers/SectionRenderer";
import { TabsRenderer } from "./containers/TabsRenderer";
import { ColumnsRenderer } from "./containers/ColumnsRenderer";
import { EmbeddedCanvasElement } from "./elements/EmbeddedCanvasElement";
import { ActionButtonElement } from "./elements/ActionButtonElement";
import { CanvasAggregateWidget } from "./elements/CanvasAggregateWidget";

/**
 * Props for ElementRenderer
 */
interface ElementRendererProps {
  /** The element to render */
  element: CanvasElement;
  /** All canvas elements (for recursive rendering) */
  allElements: CanvasElement[];
  /** Whether this element is nested inside a container */
  isNested?: boolean;
  /** Mode: edit for builder, runtime for execution */
  mode: "edit" | "runtime";
  /** Field state for form values */
  fieldState?: Record<string, FieldState>;
  /** Callback for navigating into embedded child canvas (edit mode) */
  onNavigateToChildCanvas?: (childCanvasId: string | number) => void;
}

/**
 * Helper to get MUI Icon component by name.
 */
function getMuiIcon(iconName: string): React.ElementType | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const icons = MuiIcons as Record<string, any>;
  return icons[iconName] || icons[`${iconName}Outlined`] || null;
}

/**
 * Helper to map heading level to MUI Typography level.
 */
function getHeadingLevel(
  level: number,
): "h1" | "h2" | "h3" | "h4" | "title-lg" | "title-md" {
  switch (level) {
    case 1:
      return "h1";
    case 2:
      return "h2";
    case 3:
      return "h3";
    case 4:
      return "h4";
    case 5:
      return "title-lg";
    case 6:
    default:
      return "title-md";
  }
}

/**
 * Renders text/static elements with variant support.
 */
function renderTextElement(props: CanvasElementProps): React.JSX.Element {
  const textVariant = props.textVariant || "text";

  // Common text styles
  const textStyles = {
    color: props.textColor || undefined,
    textAlign: props.textAlign || undefined,
    fontWeight: props.fontWeight || undefined,
  };

  switch (textVariant) {
    case "heading": {
      const level = props.level || 2;
      return (
        <Typography level={getHeadingLevel(level)} sx={textStyles}>
          {props.label || ""}
        </Typography>
      );
    }

    case "html": {
      return (
        <Box
          sx={{ ...textStyles }}
          dangerouslySetInnerHTML={{
            __html: props.htmlContent || props.label || "",
          }}
        />
      );
    }

    case "icon": {
      const IconComponent = props.iconName ? getMuiIcon(props.iconName) : null;
      if (!IconComponent) {
        return (
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            {props.iconName
              ? `Icon not found: ${props.iconName}`
              : "No icon specified"}
          </Typography>
        );
      }
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: props.textAlign || "center",
            ...textStyles,
          }}
        >
          <IconComponent
            sx={{
              fontSize:
                props.size === "lg" ? 48 : props.size === "sm" ? 24 : 32,
              color: props.textColor || "inherit",
            }}
          />
        </Box>
      );
    }

    case "spacer": {
      const height = props.spacerHeight || 24;
      return <Box sx={{ height, minHeight: height }} />;
    }

    case "text":
    default: {
      // Default text rendering (backward compatible)
      return (
        <Typography
          level={
            props.size === "lg"
              ? "h4"
              : props.size === "sm"
                ? "body-sm"
                : "body-md"
          }
          sx={textStyles}
        >
          {props.label || ""}
        </Typography>
      );
    }
  }
}

/**
 * ElementRenderer - Renders a single canvas element.
 */
export function ElementRenderer({
  element,
  allElements,
  isNested = false,
  mode,
  fieldState = {},
  onNavigateToChildCanvas,
}: ElementRendererProps) {
  const { type, props, layout, data_binding } = element;

  // Get field state for this element if it has a data binding
  const elementFieldState = data_binding?.field_path
    ? fieldState[data_binding.field_path]
    : undefined;

  // Render the element content based on type
  const renderContent = () => {
    switch (type) {
      case "field":
        return (
          <FieldElement
            props={props}
            isPreview={mode === "edit"}
            fieldState={elementFieldState}
          />
        );

      case "container":
      case "card": {
        const config = props as unknown as ContainerElementConfig;
        const containerType = config?.containerType || "SECTION";

        switch (containerType) {
          case "TABS":
            return (
              <TabsRenderer
                element={element}
                allElements={allElements}
                mode={mode}
                fieldState={fieldState}
              />
            );
          case "COLUMNS":
            return (
              <ColumnsRenderer
                element={element}
                allElements={allElements}
                mode={mode}
                fieldState={fieldState}
              />
            );
          case "SECTION":
          case "CARD":
          case "COLLAPSIBLE":
          default:
            return (
              <SectionRenderer
                element={element}
                allElements={allElements}
                mode={mode}
                fieldState={fieldState}
              />
            );
        }
      }

      case "tabs":
        return (
          <TabsRenderer
            element={element}
            allElements={allElements}
            mode={mode}
            fieldState={fieldState}
          />
        );

      case "text":
        return renderTextElement(props);

      case "divider":
        return <Divider sx={{ my: 1 }} />;

      case "image":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.level1",
              borderRadius: "sm",
            }}
          >
            {props.src ? (
              <img
                src={props.src as string}
                alt={props.label || "Image"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                {mode === "edit" ? "Image placeholder" : "No image"}
              </Typography>
            )}
          </Box>
        );

      case "embedded_canvas":
        // In edit mode, show the EmbeddedCanvasElement preview component
        // Runtime mode is handled by CanvasRenderer using EmbeddedCanvasRenderer
        return (
          <EmbeddedCanvasElement
            element={element}
            mode={mode}
            isSelected={false}
            onNavigateToChild={onNavigateToChildCanvas}
          />
        );

      case "widget":
        return (
          <CanvasAggregateWidget
            mode={mode}
            element={
              {
                id: element.id,
                type: "widget",
                props,
                gridColumn: layout.gridColumn || 1,
                gridRow: layout.gridRow || 1,
                columnSpan: layout.columnSpan || 3,
                rowSpan: layout.rowSpan || 2,
              } as RenderableElement
            }
          />
        );

      case "timeline":
        return (
          <Sheet variant="outlined" sx={{ p: 2, borderRadius: "sm" }}>
            <Typography level="title-sm">
              {String(props.title || "Timeline")}
            </Typography>
            <Typography level="body-sm">
              Timeline from {String(props.data_source_alias || "main")}
            </Typography>
          </Sheet>
        );

      case "view_grid": {
        const gridTitle =
          typeof props.title === "string" ? props.title : undefined;
        const gridLabel =
          typeof props.label === "string" ? props.label : undefined;
        const dataSourceAlias =
          typeof props.data_source_alias === "string"
            ? props.data_source_alias
            : "main";

        return (
          <Sheet
            variant="soft"
            color="primary"
            sx={{
              height: "100%",
              minHeight: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "sm",
              border: "2px dashed",
              borderColor: "primary.300",
              p: 2,
              gap: 1,
              textAlign: "center",
            }}
          >
            <GridOnIcon sx={{ fontSize: 28, color: "primary.500" }} />
            <Typography level="body-sm" fontWeight="md">
              {gridTitle || gridLabel || "View Grid"}
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Browse records from {dataSourceAlias}
            </Typography>
          </Sheet>
        );
      }

      case "action_button":
        return (
          <ActionButtonElement
            props={props}
            isPreview={mode === "edit"}
            elementId={element.id}
          />
        );

      default:
        return (
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            Unknown element: {type}
          </Typography>
        );
    }
  };

  // Grid positioning
  const gridColumn = layout?.gridColumn || 1;
  const gridRow = layout?.gridRow || 1;
  const columnSpan = layout?.columnSpan || 6;
  const rowSpan = layout?.rowSpan || 1;

  // For nested elements, use simpler positioning (just span)
  const gridStyle = isNested
    ? {
        gridColumn: `span ${columnSpan}`,
        minWidth: 0,
      }
    : {
        gridColumn: `${gridColumn} / span ${columnSpan}`,
        gridRow: `${gridRow} / span ${rowSpan}`,
        minWidth: 0,
      };

  return (
    <Box
      sx={{
        ...gridStyle,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {renderContent()}
    </Box>
  );
}

/**
 * Get a file type icon based on content_type.
 */
function getFileTypeIcon(contentType: string): React.ReactElement {
  if (contentType === "application/pdf") {
    return <PictureAsPdfIcon sx={{ color: "danger.500" }} />;
  }
  if (contentType.startsWith("image/")) {
    return <ImageIcon sx={{ color: "primary.500" }} />;
  }
  if (
    contentType.startsWith("application/vnd.openxmlformats") ||
    contentType === "application/msword"
  ) {
    return <DescriptionIcon sx={{ color: "primary.600" }} />;
  }
  return <InsertDriveFileIcon sx={{ color: "neutral.500" }} />;
}

/**
 * FileFieldElement - Renders a file upload/display field for canvas forms.
 */
interface FileFieldElementProps {
  value: unknown;
  disabled?: boolean;
  onChange?: (value: unknown) => void;
  onBlur?: () => void;
}

function FileFieldElement({
  value,
  disabled,
  onChange,
}: FileFieldElementProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Parse FileReference from value
  let fileRef: FileReference | null = null;
  if (value) {
    try {
      if (typeof value === "string") {
        fileRef = JSON.parse(value) as FileReference;
      } else if (typeof value === "object") {
        fileRef = value as FileReference;
      }
    } catch {
      // Not valid JSON
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // For canvas forms, we store the File object; the submission handler
    // is responsible for uploading it via the API. We create a partial
    // FileReference with local info so the UI can display the filename.
    const localRef: Partial<FileReference> & { _localFile: File } = {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      _localFile: file,
    };
    onChange?.(localRef);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    onChange?.(null);
  };

  // File exists -- show file info
  if (fileRef && fileRef.filename) {
    return (
      <Sheet
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: "sm",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {getFileTypeIcon(fileRef.content_type || "")}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Link
            level="body-sm"
            fontWeight="md"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileRef.filename}
          </Link>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            {formatFileSize(fileRef.size_bytes || 0)}
          </Typography>
        </Box>
        {!disabled && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleFileSelect}
            />
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => fileInputRef.current?.click()}
            >
              Replace
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="danger"
              onClick={handleRemove}
              startDecorator={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            >
              Delete
            </Button>
          </>
        )}
      </Sheet>
    );
  }

  // No file -- show upload zone
  return (
    <Sheet
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: "sm",
        borderStyle: "dashed",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        "&:hover": disabled ? {} : { bgcolor: "background.level1" },
      }}
      onClick={() => {
        if (!disabled) fileInputRef.current?.click();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <UploadFileIcon sx={{ fontSize: 32, color: "neutral.400" }} />
      <Typography level="body-sm" sx={{ color: "text.secondary" }}>
        Drop file here or click to upload
      </Typography>
    </Sheet>
  );
}

/**
 * FieldElement - Renders form field elements.
 */
interface FieldElementProps {
  props: CanvasElementProps;
  isPreview: boolean;
  fieldState?: FieldState;
}

function FieldElement({ props, isPreview, fieldState }: FieldElementProps) {
  const { value, error, touched, onChange, onBlur } = fieldState || {};
  const showError = touched && !!error;
  const isDisabled = isPreview || props.disabled || props.readonly;
  const isReadOnly = isPreview || Boolean(props.readonly);

  // Determine input type from variant or inputType prop
  const inputType =
    (props.variant as string) || (props.inputType as string) || "text";

  const renderLabel = () => (
    <FormLabel>
      {props.label}
      {props.required && (
        <Typography component="span" sx={{ color: "danger.500" }}>
          {" "}
          *
        </Typography>
      )}
    </FormLabel>
  );

  const renderHelper = () => {
    if (showError) {
      return <FormHelperText>{error}</FormHelperText>;
    }
    if (props.helperText) {
      return <FormHelperText>{props.helperText}</FormHelperText>;
    }
    return null;
  };

  // Route based on input type
  switch (inputType) {
    case "textarea":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Textarea
            size="sm"
            value={(value as string) || ""}
            placeholder={props.placeholder}
            disabled={Boolean(props.disabled)}
            slotProps={{
              textarea: {
                readOnly: isReadOnly && !props.disabled,
                "aria-readonly": isReadOnly && !props.disabled,
                disabled: Boolean(props.disabled),
              },
            }}
            minRows={props.rows || 3}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "number":
    case "number-input":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="number"
            value={value !== undefined && value !== null ? String(value) : ""}
            placeholder={props.placeholder || "0"}
            disabled={isDisabled}
            onChange={(e) => {
              const numValue = e.target.value ? Number(e.target.value) : null;
              onChange?.(numValue);
            }}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "date":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="date"
            value={value ? String(value).split("T")[0] : ""}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "datetime":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            type="datetime-local"
            value={value ? String(value).slice(0, 16) : ""}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "checkbox":
    case "boolean":
      return (
        <FormControl error={showError}>
          <Checkbox
            size="sm"
            label={
              <span>
                {props.label}
                {props.required && (
                  <Typography component="span" sx={{ color: "danger.500" }}>
                    {" "}
                    *
                  </Typography>
                )}
              </span>
            }
            checked={!!value}
            disabled={isDisabled}
            onChange={(e) => onChange?.(e.target.checked)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "select":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Select
            size="sm"
            value={value != null ? String(value) : null}
            placeholder={props.placeholder || "Select..."}
            disabled={isDisabled}
            onChange={(_, newValue) => onChange?.(newValue)}
            onClose={onBlur}
          >
            {props.options?.map((opt) => (
              <Option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </Option>
            )) || (
              <Option value="" disabled>
                No options
              </Option>
            )}
          </Select>
          {renderHelper()}
        </FormControl>
      );

    case "file":
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <FileFieldElement
            value={value}
            disabled={isDisabled}
            onChange={onChange}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );

    case "text":
    case "text-input":
    default:
      return (
        <FormControl error={showError}>
          {renderLabel()}
          <Input
            size="sm"
            value={(value as string) || ""}
            placeholder={props.placeholder || "Enter text..."}
            disabled={Boolean(props.disabled)}
            slotProps={{
              input: {
                readOnly: isReadOnly && !props.disabled,
                "aria-readonly": isReadOnly && !props.disabled,
                disabled: Boolean(props.disabled),
              },
            }}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
          {renderHelper()}
        </FormControl>
      );
  }
}

// ActionButtonElement lives in ./elements/ActionButtonElement so both this
// renderer and CanvasRenderer's runtime switch can share it.

export default ElementRenderer;
